import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from 'react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  api,
  API_BASE_URL,
  API_FALLBACK_BASE_URL,
  ConnectionTestResponse,
  HealthResponse,
  Item,
  setAuthTokenProvider,
  setUserObjectIdProvider
} from './api';
import type { ExternalIdConfiguration } from './authConfig';
import { featureGroups, featureMap, features } from './features';
import type { FeatureModule } from './features/types';

interface AppProps {
  authConfig: ExternalIdConfiguration;
}

const DEFAULT_SIDEBAR_WIDTH = 260;
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 360;
const COLLAPSED_SIDEBAR_WIDTH = 92;

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
};

function createIcon(paths: ReactNode): () => JSX.Element {
  return function Icon() {
    return (
      <svg className="feature-nav__icon" viewBox="0 0 24 24" role="presentation" aria-hidden="true">
        {paths}
      </svg>
    );
  };
}

const sidebarIcons: Record<string, () => JSX.Element> = {
  overview: createIcon(
    <>
      <path d="M4 11.5 12 5l8 6.5" {...strokeProps} />
      <path d="M6 10.5v8.5h5v-5h2v5h5v-8.5" {...strokeProps} />
    </>
  ),
  'api-tester': createIcon(
    <>
      <rect x="3.5" y="6.5" width="17" height="11" rx="2" {...strokeProps} />
      <path d="m8 11 2 2-2 2" {...strokeProps} />
      <path d="M12.5 15h3.5" {...strokeProps} />
    </>
  ),
  health: createIcon(
    <path
      d="M12 19s-7-4.35-7-9a4 4 0 0 1 7-2 4 4 0 0 1 7 2c0 4.65-7 9-7 9Z"
      {...strokeProps}
    />
  ),
  connectivity: createIcon(
    <>
      <path d="M10 13.5 8.5 15a3.5 3.5 0 0 0 5 5l1.5-1.5" {...strokeProps} />
      <path d="M14 10.5 15.5 9a3.5 3.5 0 0 0-5-5L9 5.5" {...strokeProps} />
      <path d="M8.5 8.5 15.5 15.5" {...strokeProps} />
    </>
  ),
  'sample-data': createIcon(
    <>
      <ellipse cx="12" cy="6.2" rx="6.5" ry="2.8" {...strokeProps} />
      <path d="M5.5 6.2V17.8C5.5 19.5 8.4 21 12 21s6.5-1.5 6.5-3.2V6.2" {...strokeProps} />
      <path d="M5.5 12c0 1.7 2.9 3.2 6.5 3.2s6.5-1.5 6.5-3.2" {...strokeProps} />
    </>
  ),
  tasks: createIcon(
    <>
      <path d="M6 5h12a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" {...strokeProps} />
      <path d="m8 11 2 2 4-4" {...strokeProps} />
    </>
  ),
  'workflow-builder': createIcon(
    <>
      <rect x="4" y="4" width="6.5" height="6.5" rx="1.4" {...strokeProps} />
      <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.4" {...strokeProps} />
      <rect x="9" y="13.5" width="6.5" height="6.5" rx="1.4" {...strokeProps} />
      <path d="M10.5 7.25h3" {...strokeProps} />
      <path d="M12 10.5v2.25" {...strokeProps} />
      <path d="M15.5 7.25v5.25" {...strokeProps} />
    </>
  ),
  kingdom: createIcon(
    <>
      <path d="M4 9 6.5 7l3 3 3-5 3 5 3-3 2.5 2v8H4V9Z" {...strokeProps} />
      <path d="M4 14h16" {...strokeProps} />
    </>
  )
};

const fallbackIcon = createIcon(
  <>
    <circle cx="12" cy="12" r="8.5" {...strokeProps} />
    <path d="M12 8v4l2.5 2.5" {...strokeProps} />
  </>
);

function getHashSlug(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const normalizedHash = window.location.hash.replace(/^#/, '').trim().toLowerCase();
  if (!normalizedHash) {
    return '';
  }

  const withoutLeadingSlash = normalizedHash.replace(/^\//, '');
  const [firstSegment] = withoutLeadingSlash.split('/');
  return firstSegment ?? '';
}

function getHashForFeature(slug: string): string {
  return `#/${slug}`;
}

function featureIsAccessible(feature: FeatureModule, isAuthenticated: boolean): boolean {
  if (isAuthenticated) {
    return true;
  }

  return feature.config.availableWhenSignedOut;
}

function sanitizeSlug(slug: string): string {
  if (slug && featureMap.has(slug)) {
    return slug;
  }

  return features[0]?.config.slug ?? '';
}

function ensureAccessibleSlug(slug: string, isAuthenticated: boolean): string {
  const candidate = featureMap.get(slug);
  if (candidate && featureIsAccessible(candidate, isAuthenticated)) {
    return slug;
  }

  const fallback = features.find((feature) => featureIsAccessible(feature, isAuthenticated));
  return fallback ? fallback.config.slug : slug;
}

function formatFeatureType(value: string): string {
  if (!value) {
    return 'General';
  }

  return value
    .split(/[-_\s]+/)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export default function App({ authConfig }: AppProps) {
  const { loginRequest, externalIdPolicies } = authConfig;
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [connectionTest, setConnectionTest] = useState<ConnectionTestResponse | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFeatureSlug, setActiveFeatureSlug] = useState<string>(() => sanitizeSlug(getHashSlug()));
  const [authError, setAuthError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia('(max-width: 900px)').matches;
  });
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const lastExpandedWidthRef = useRef(DEFAULT_SIDEBAR_WIDTH);
  const sidebarWidthRef = useRef(DEFAULT_SIDEBAR_WIDTH);
  const resizeDataRef = useRef<{ startX: number; startWidth: number }>({
    startX: 0,
    startWidth: DEFAULT_SIDEBAR_WIDTH
  });

  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isAuthFlowInProgress = inProgress !== InteractionStatus.None;

  const usesFallbackProxy = API_BASE_URL !== API_FALLBACK_BASE_URL;
  const isAbsoluteApiBase = /^https?:\/\//i.test(API_BASE_URL);
  const shouldExplainFallback = usesFallbackProxy && isAbsoluteApiBase;

  const activeAccount = useMemo(
    () => instance.getActiveAccount() ?? accounts[0] ?? null,
    [instance, accounts]
  );
  const accountLabel = useMemo(() => getAccountLabel(activeAccount), [activeAccount]);
  const userObjectId = useMemo(() => {
    if (!activeAccount) {
      return null;
    }

    const claims = activeAccount.idTokenClaims as Record<string, unknown> | undefined;
    const oid = claims && typeof claims.oid === 'string' ? claims.oid : undefined;
    const sub = claims && typeof claims.sub === 'string' ? claims.sub : undefined;

    return oid ?? sub ?? activeAccount.localAccountId ?? activeAccount.homeAccountId ?? null;
  }, [activeAccount]);

  useEffect(() => {
    if (userObjectId) {
      setUserObjectIdProvider(() => userObjectId);
    } else {
      setUserObjectIdProvider(null);
    }

    return () => {
      setUserObjectIdProvider(null);
    };
  }, [userObjectId]);

  useEffect(() => {
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (isAuthFlowInProgress) {
      return;
    }

    if (isAuthenticated) {
      setAuthError(null);
    }

    setActiveFeatureSlug((current) => ensureAccessibleSlug(current, isAuthenticated));
  }, [isAuthenticated, isAuthFlowInProgress]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    function handleHashChange() {
      const slugFromHash = sanitizeSlug(getHashSlug());

      if (isAuthFlowInProgress) {
        setActiveFeatureSlug(slugFromHash);
        return;
      }

      setActiveFeatureSlug(ensureAccessibleSlug(slugFromHash, isAuthenticated));
    }

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [isAuthenticated, isAuthFlowInProgress]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 900px)');
    const handleChange = (event: MediaQueryList | MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    handleChange(mediaQuery);

    const listener = (event: MediaQueryListEvent) => handleChange(event);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener);
      return () => {
        mediaQuery.removeEventListener('change', listener);
      };
    }

    mediaQuery.addListener(listener);

    return () => {
      mediaQuery.removeListener(listener);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsMobileNavOpen(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const targetHash = getHashForFeature(activeFeatureSlug);

    if (window.location.hash !== targetHash) {
      window.location.hash = targetHash;
    }
  }, [activeFeatureSlug]);

  const acquireAccessToken = useCallback(async () => {
    if (!activeAccount) {
      throw new Error('No active Microsoft Entra External ID account is available.');
    }

    try {
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account: activeAccount
      });

      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({
          ...loginRequest,
          account: activeAccount
        });

        throw new Error('Redirecting for interactive authentication.');
      }

      throw error instanceof Error ? error : new Error(String(error));
    }
  }, [activeAccount, instance, loginRequest]);

  useEffect(() => {
    if (!activeAccount || !isAuthenticated) {
      setHealth(null);
      setItems([]);
      setConnectionTest(null);
      setLoading(false);
      setError(null);
      setAuthTokenProvider(null);
      return;
    }

    let cancelled = false;

    const provider = async () => {
      const token = await acquireAccessToken();
      return token.length > 0 ? token : null;
    };

    setAuthTokenProvider(provider);

    async function bootstrap() {
      setError(null);
      setLoading(true);

      try {
        const results = await Promise.allSettled([
          api.health(),
          api.listItems(),
          api.connectionTest()
        ]);
        const [
          healthResult,
          itemsResult,
          connectionResult
        ] = results as [
          PromiseSettledResult<HealthResponse>,
          PromiseSettledResult<Item[]>,
          PromiseSettledResult<ConnectionTestResponse>
        ];

        if (cancelled) {
          return;
        }

        const errors: string[] = [];

        if (healthResult.status === 'fulfilled') {
          setHealth(healthResult.value);
        } else {
          setHealth(null);
          errors.push(`Failed to load backend health: ${normalizeError(healthResult.reason)}`);
        }

        if (itemsResult.status === 'fulfilled') {
          setItems(itemsResult.value);
        } else {
          setItems([]);
          errors.push(`Failed to load items: ${normalizeError(itemsResult.reason)}`);
        }

        if (connectionResult.status === 'fulfilled') {
          setConnectionTest(connectionResult.value);
        } else {
          setConnectionTest(null);
          errors.push(
            `Failed to verify backend connectivity: ${normalizeError(connectionResult.reason)}`
          );
        }

        if (errors.length > 0) {
          setError(errors.join(' | '));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
      setAuthTokenProvider(null);
    };
  }, [activeAccount, acquireAccessToken, isAuthenticated]);

  const handleLogin = useCallback(() => {
    setAuthError(null);
    instance.loginRedirect(loginRequest).catch((err: unknown) => {
      console.error('Microsoft Entra External ID login failed', err);
      setAuthError(normalizeError(err));
    });
  }, [instance, loginRequest]);

  const handleLogout = useCallback(() => {
    setAuthTokenProvider(null);
    instance
      .logoutRedirect({ authority: externalIdPolicies.signUpSignIn.authority })
      .catch((err: unknown) => {
        console.error('Microsoft Entra External ID logout failed', err);
        setError(`Sign-out failed: ${normalizeError(err)}`);
      });
  }, [instance, externalIdPolicies]);

  function normalizeError(reason: unknown): string {
    if (reason instanceof Error) {
      return reason.message;
    }

    try {
      return JSON.stringify(reason);
    } catch {
      return String(reason);
    }
  }

  const navGroups = useMemo(() => {
    const groups: Array<{ type: string; label: string; features: FeatureModule[] }> = [];

    for (const [type, groupFeatures] of featureGroups.entries()) {
      const visible = groupFeatures.filter((feature) => featureIsAccessible(feature, isAuthenticated));

      if (visible.length === 0) {
        continue;
      }

      groups.push({
        type,
        label: formatFeatureType(type),
        features: visible
      });
    }

    return groups;
  }, [isAuthenticated]);

  const fallbackFeature = useMemo(() => {
    return (
      features.find((feature) => featureIsAccessible(feature, isAuthenticated)) ?? features[0] ?? null
    );
  }, [isAuthenticated]);

  const activeFeature = featureMap.get(activeFeatureSlug) ?? fallbackFeature;

  const FeatureComponent = activeFeature?.Component;

  const acquireTokenProvider = useMemo(
    () => (isAuthenticated && activeAccount ? acquireAccessToken : null),
    [isAuthenticated, activeAccount, acquireAccessToken]
  );

  const featureContext = useMemo(
    () => ({
      isAuthenticated,
      apiBaseUrl: API_BASE_URL,
      fallbackBaseUrl: API_FALLBACK_BASE_URL,
      shouldExplainFallback,
      userObjectId,
      health,
      connectionTest,
      items,
      itemsLoading: loading,
      itemsError: error,
      acquireAccessToken: acquireTokenProvider
    }),
    [
      isAuthenticated,
      shouldExplainFallback,
      userObjectId,
      health,
      connectionTest,
      items,
      loading,
      error,
      acquireTokenProvider
    ]
  );

  const sessionLabel = isAuthenticated ? accountLabel : 'Guest user';
  const activeFeatureTypeLabel = activeFeature
    ? formatFeatureType(activeFeature.config.featureType)
    : 'General';
  const computedSidebarWidth = sidebarCollapsed ? COLLAPSED_SIDEBAR_WIDTH : sidebarWidth;
  const resizerWidth = sidebarCollapsed ? 0 : 8;
  const shellStyle = useMemo(
    () =>
      ({
        '--sidebar-width': `${computedSidebarWidth}px`,
        '--resizer-width': `${resizerWidth}px`
      }) as CSSProperties,
    [computedSidebarWidth, resizerWidth]
  );
  const sidebarToggleLabel = sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar';
  const sidebarToggleIcon = sidebarCollapsed ? '›' : '‹';
  const mobileNavToggleLabel = isMobileNavOpen ? 'Hide navigation' : 'Show navigation';

  const handleToggleSidebar = useCallback(() => {
    if (isMobileViewport) {
      setIsMobileNavOpen((previous) => !previous);
      return;
    }

    setSidebarCollapsed((previous) => {
      if (previous) {
        const restoredWidth = Math.min(
          SIDEBAR_MAX_WIDTH,
          Math.max(SIDEBAR_MIN_WIDTH, lastExpandedWidthRef.current || DEFAULT_SIDEBAR_WIDTH)
        );
        setSidebarWidth(restoredWidth);
        return false;
      }

      lastExpandedWidthRef.current = sidebarWidthRef.current;
      return true;
    });
  }, [isMobileViewport]);

  const handleSidebarResizeStart = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (sidebarCollapsed || isMobileViewport) {
        return;
      }

      event.preventDefault();
      resizeDataRef.current = {
        startX: event.clientX,
        startWidth: sidebarWidthRef.current
      };
      setIsResizingSidebar(true);
    },
    [sidebarCollapsed, isMobileViewport]
  );

  const handleFeatureSelect = useCallback(
    (slug: string) => {
      setActiveFeatureSlug(slug);

      if (isMobileViewport) {
        setIsMobileNavOpen(false);
      }
    },
    [isMobileViewport]
  );

  const shellClasses = ['app-shell'];

  if (sidebarCollapsed) {
    shellClasses.push('app-shell--collapsed');
  }

  if (isMobileViewport) {
    shellClasses.push('app-shell--mobile');
  }

  if (isMobileViewport && isMobileNavOpen) {
    shellClasses.push('app-shell--mobile-nav-open');
  }

  const sidebarStyle = isMobileViewport ? undefined : { width: computedSidebarWidth };

  useEffect(() => {
    sidebarWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  useEffect(() => {
    if (!isResizingSidebar) {
      document.body.style.userSelect = '';
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (sidebarCollapsed) {
        return;
      }

      const { startX, startWidth } = resizeDataRef.current;
      const delta = event.clientX - startX;
      let nextWidth = startWidth + delta;
      nextWidth = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, nextWidth));
      sidebarWidthRef.current = nextWidth;
      setSidebarWidth(nextWidth);
    };

    const handlePointerUp = () => {
      setIsResizingSidebar(false);
      lastExpandedWidthRef.current = sidebarWidthRef.current;
    };

    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.userSelect = '';
    };
  }, [isResizingSidebar, sidebarCollapsed]);

  if (isAuthFlowInProgress) {
    return (
      <div className="auth-screen auth-screen--loading">
        <div className="auth-card">
          <h1>Signing you in…</h1>
          <p>Please wait while we verify your Microsoft Entra External ID session.</p>
        </div>
      </div>
    );
  }

  if (!activeFeature || !FeatureComponent) {
    return (
      <main>
        <div className="app-status" role="alert" aria-live="polite">
          <p className="app-status__message app-status__message--error">
            No features are available. Update the feature configuration to continue.
          </p>
        </div>
      </main>
    );
  }

  return (
    <div className={shellClasses.join(' ')} style={shellStyle}>
      <aside
        className={`app-sidebar${sidebarCollapsed ? ' app-sidebar--collapsed' : ''}`}
        style={sidebarStyle}
        id="app-sidebar"
        aria-hidden={isMobileViewport && !isMobileNavOpen}
      >
        <div className="app-sidebar__header">
          <div className="app-sidebar__brand">
            <div className="app-sidebar__logo" aria-hidden="true">
              PH
            </div>
            <div className="app-sidebar__title">
              <span>Project Hermes</span>
              <small>Control Center</small>
            </div>
          </div>
          <button
            type="button"
            className="sidebar-toggle"
            onClick={handleToggleSidebar}
            aria-label={sidebarToggleLabel}
            aria-expanded={!sidebarCollapsed}
          >
            <span className="sidebar-toggle__icon" aria-hidden="true">
              {sidebarToggleIcon}
            </span>
          </button>
        </div>

        <nav className="feature-nav" aria-label="Primary">
          {navGroups.map((group) => (
            <div key={group.type} className="feature-nav__group">
              <span className="feature-nav__group-label">{group.label}</span>
              <ul className="feature-nav__list">
                {group.features.map((feature) => {
                  const slug = feature.config.slug;
                  const isActive = slug === activeFeature.config.slug;
                  const Icon = sidebarIcons[slug] ?? fallbackIcon;
                  return (
                    <li key={slug}>
                      <a
                        href={getHashForFeature(slug)}
                        className={`feature-nav__link${isActive ? ' feature-nav__link--active' : ''}`}
                        onClick={() => handleFeatureSelect(slug)}
                        title={feature.config.name}
                      >
                        <Icon />
                        <span className="feature-nav__label">{feature.config.name}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="app-sidebar__session">
          <div className="app-sidebar__session-info">
            <span className="app-sidebar__session-label">
              {isAuthenticated ? 'Signed in' : 'Guest access'}
            </span>
            <strong className="app-sidebar__session-name">{sessionLabel}</strong>
            {isAuthenticated && userObjectId && (
              <span className="app-sidebar__session-id">{userObjectId}</span>
            )}
            {!isAuthenticated && (
              <p className="app-sidebar__session-hint">
                Sign in with Microsoft Entra External ID to unlock additional features.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={isAuthenticated ? handleLogout : handleLogin}
            className={`${isAuthenticated ? 'sign-out-button' : 'primary-button'} app-sidebar__session-action`}
          >
            {isAuthenticated ? 'Sign out' : 'Sign in'}
          </button>
          {!isAuthenticated && authError && (
            <p role="alert" className="app-sidebar__error">
              {authError}
            </p>
          )}
        </div>
      </aside>

      {isMobileViewport && (
        <button
          type="button"
          className="app-mobile-overlay"
          onClick={() => setIsMobileNavOpen(false)}
          aria-hidden={!isMobileNavOpen}
          tabIndex={isMobileNavOpen ? 0 : -1}
        >
          <span className="sr-only">Close navigation</span>
        </button>
      )}

      {!isMobileViewport && (
        <div
          className={`app-resizer${sidebarCollapsed ? ' app-resizer--hidden' : ''}${
            isResizingSidebar ? ' app-resizer--active' : ''
          }`}
          role="separator"
          aria-orientation="vertical"
          aria-hidden={sidebarCollapsed}
          onPointerDown={handleSidebarResizeStart}
        />
      )}

      <main className="app-main">
        {isMobileViewport && (
          <div className="app-mobile-toolbar">
            <button
              type="button"
              className={`mobile-nav-toggle${isMobileNavOpen ? ' mobile-nav-toggle--open' : ''}`}
              onClick={() => setIsMobileNavOpen((previous) => !previous)}
              aria-expanded={isMobileNavOpen}
              aria-controls="app-sidebar"
            >
              <span className="mobile-nav-toggle__icon" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              <span className="mobile-nav-toggle__label">{mobileNavToggleLabel}</span>
            </button>
          </div>
        )}
        <div className="app-main__inner">
          <header className="app-header">
            <span className="app-header__eyebrow">{activeFeatureTypeLabel}</span>
            <h1>{activeFeature.config.name}</h1>
            <p>{activeFeature.config.description}</p>
          </header>

          {!isAuthenticated && (
            <div className="app-notice app-notice--info">
              <div>
                <strong>Limited access</strong>
                <p>Sign in with Microsoft Entra External ID to unlock additional features.</p>
              </div>
            </div>
          )}

          <AppStatus loading={loading && isAuthenticated} error={error} />

          <div className="feature-content">
            <FeatureComponent {...featureContext} />
          </div>
        </div>
      </main>
    </div>
  );
}

interface AppStatusProps {
  loading: boolean;
  error: string | null;
}

function AppStatus({ loading, error }: AppStatusProps) {
  if (!loading && !error) {
    return null;
  }

  const role = error ? 'alert' : 'status';

  return (
    <div className="app-status" role={role} aria-live="polite">
      {loading && <p className="app-status__message">Loading data from the FastAPI backend…</p>}
      {error && <p className="app-status__message app-status__message--error">{error}</p>}
    </div>
  );
}

function getAccountLabel(account: AccountInfo | null): string {
  if (!account) {
    return 'Authenticated user';
  }

  const candidates: Array<string | undefined> = [];

  const claims = account.idTokenClaims as Record<string, unknown> | undefined;

  candidates.push(account.name);
  candidates.push(typeof account.username === 'string' ? account.username : undefined);

  if (claims) {
    const claimName = typeof claims.name === 'string' ? claims.name : undefined;
    const givenName = typeof claims.given_name === 'string' ? claims.given_name : undefined;
    const familyName = typeof claims.family_name === 'string' ? claims.family_name : undefined;
    const preferredUsername = typeof claims.preferred_username === 'string' ? claims.preferred_username : undefined;
    const emailClaim = typeof claims.email === 'string' ? claims.email : undefined;

    let combinedGivenFamily: string | undefined;

    if (givenName || familyName) {
      combinedGivenFamily = [givenName, familyName].filter(Boolean).join(' ');
    }

    candidates.push(claimName);
    candidates.push(combinedGivenFamily);
    candidates.push(preferredUsername);
    candidates.push(emailClaim);

    const emailsClaim = Array.isArray(claims.emails) ? claims.emails : undefined;

    if (emailsClaim) {
      const firstEmail = emailsClaim.find((value) => typeof value === 'string' && value.trim().length > 0);
      candidates.push(firstEmail as string | undefined);
    }
  }

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const trimmed = candidate.trim();

    if (trimmed.length === 0) {
      continue;
    }

    if (trimmed.toLowerCase() === 'unknown') {
      continue;
    }

    return trimmed;
  }

  return 'Authenticated user';
}
